import { type ZodType, type ZodTypeDef, z } from 'zod'
import { requestExplorerJson } from '../request'
import type { HeadersInput } from '../types'

export type RoutescanClient = {
  get: <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(parameters: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: HeadersInput
  }) => Promise<Output>
}

type CreateRoutescanClientParameters = {
  chainId: number
  network?: 'mainnet'
  apiKey?: string
  proxyUrl?: string
}

/**
 * Routescan の Etherscan-compatible API client を作成する。
 *
 * @param params - Routescan API 接続設定
 * @param params.chainId - URL path に埋め込む EVM chain ID
 * @param params.network - Routescan network 名
 * @param params.apiKey - 任意の Routescan API key
 * @param params.proxyUrl - Explorer API を中継するプロキシ URL
 * @returns Routescan API client
 */
export const createRoutescanClient = ({
  chainId,
  network = 'mainnet',
  apiKey,
  proxyUrl,
}: CreateRoutescanClientParameters): RoutescanClient => {
  const apiBaseUrl = `https://api.routescan.io/v2/network/${network}/evm/${chainId}/etherscan/api`

  const get = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >({
    path,
    query,
    resultSchema,
    headers,
  }: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: HeadersInput
  }): Promise<Output> => {
    const responseSchema = z.object({
      status: z.string(),
      message: z.string(),
      result: z.unknown(),
    })

    const url = new URL(path, apiBaseUrl)
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value)
    }
    if (apiKey !== undefined) {
      url.searchParams.set('apikey', apiKey)
    }
    console.debug(`[GET] ${url.toString()}`)
    const { status, message, result } = responseSchema.parse(
      await requestExplorerJson({
        url:
          proxyUrl === undefined
            ? url.toString()
            : `${proxyUrl}${encodeURIComponent(url.toString())}`,
        // Routescan の制限は chain ではなく送信元単位なので、全 chain で送信枠と 429 の待機を共有する。
        key: `routescan:${proxyUrl ?? apiKey ?? 'public'}`,
        intervalMs: 500,
        headers,
      }),
    )
    if (status !== '1') {
      throw typeof result === 'string' ? new Error(result) : new Error(message)
    }
    if (result === null || result === undefined) {
      throw new Error(message)
    }
    // block 番号など文字列が正常な result の API もあるため、先に resultSchema で検証する。
    const parsed = resultSchema.safeParse(result)
    if (parsed.success) {
      return parsed.data
    }
    if (typeof result === 'string') {
      throw new TypeError(result)
    }
    throw parsed.error
  }

  return {
    get,
  }
}
